import React from "react";
import { Handle, Position, NodeProps } from "reactflow";
import Confirmation from "@components/common/ConfirmDialog";
import { EXCLUDE_KEYS, handlerStyle } from "../common/commonData";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import { toast } from "@components/ui/use-toast";
import { RefreshCw, Copy, Fingerprint, Plus } from "lucide-react";
import { fetchConfigPage } from "../../plugin/createPluginApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { useNavigate } from "react-router-dom";
import { Button } from "@components/ui/button";

interface AuthenticationNodeData {
  header: string;
  configData?: any;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  pluginId?: string;
  configurations?: any[];
  borderColor?: string;
  shadowColor?: string;
  // Execution-related properties
  executionStatus?: "pending" | "running" | "success" | "error";
  executionResult?: any;
  response?: any;
  outputData?: any;
  inputData?: any;
  error?: string;
  // State tracking
  isDeleted?: boolean;
  lastUpdated?: number;
  parameters?: any;
}

const ConfigutrationNode: React.FC<NodeProps<AuthenticationNodeData>> = ({
  id,
  data,
}) => {
  const [isDelete, setIsDelete] = React.useState(false);
  const [deleteNodeId, setDeleteNodeId] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [isFetchingConfigs, setIsFetchingConfigs] = React.useState(false);
  const [showLoadingBorder, setShowLoadingBorder] = React.useState(false);
  const [localConfigs, setLocalConfigs] = React.useState<any[]>(
    data.configurations || [],
  );

  const navigate = useNavigate();

  // Tracks whether the user explicitly picked "No Configuration" so the
  // auto-select effect below doesn't immediately override that choice
  // when there's only one config in the list. Without this, clearing the
  // selection and having exactly one config available looks identical
  // to "never selected anything" — both have a falsy selectedUid — so
  // the effect would instantly re-select the only config and silently
  // undo the user's explicit "unselect" action.
  const userClearedSelectionRef = React.useRef(false);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  React.useEffect(() => {
    handleFetchConfigs();
  }, []);

  React.useEffect(() => {
    if (data.configurations && data.configurations.length > 0) {
      setLocalConfigs(data.configurations);
    }
  }, [data.configurations]);

  const selectedUid = data.configData?.uid || data.parameters?.uid;

  // Check if configuration was deleted - only use explicit isDeleted flag
  // This must be declared before the auto-select effect below, since
  // that effect reads isDeleted.
  const isDeleted = data.isDeleted === true;

  // Auto-select logic:
  // - If the user just deliberately cleared the selection (picked "No
  //   Configuration"), do nothing — respect that choice.
  // - Exactly 1 config available -> select it automatically, even if
  //   the node is currently marked deleted (selecting is what clears
  //   the deleted state via handleSelectConfig's onDataChange call).
  // - 2+ configs available -> only keep the selection if it's already
  //   validly saved (a non-deleted uid matching one of the available
  //   configs); otherwise leave it unselected so the user chooses.
  React.useEffect(() => {
    if (!localConfigs || localConfigs.length === 0) return;
    if (userClearedSelectionRef.current) return; // respect the user's explicit "none" choice

    const hasValidSavedSelection =
      !isDeleted &&
      !!selectedUid &&
      localConfigs.some((c) => c.uid === selectedUid);

    if (hasValidSavedSelection) return; // already saved — keep as-is

    if (localConfigs.length === 1) {
      handleSelectConfig(localConfigs[0].uid, { silent: true });
      return;
    }
  }, [localConfigs, selectedUid, isDeleted]);

  // Prepare display data for InputViewer - mask encrypted fields
  const displayConfigData = React.useMemo(() => {
    if (!data.configData) return {};

    // Get encrypted field keys from isDecript array
    const encryptedFields = data.configData?.isDecript || [];

    const displayData: any = {};
    const excludedKeys = EXCLUDE_KEYS.filter((key) => key !== "title");
    Object.entries(data.configData)
      .filter(([key]) => !excludedKeys.includes(key))
      .forEach(([key, value]) => {
        // Mask encrypted fields
        if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
          displayData[key] = "********************";
        } else {
          displayData[key] = value;
        }
      });

    return displayData;
  }, [data.configData, data.lastUpdated]); // Include lastUpdated to force re-computation

  const loadingBorderColor = data?.borderColor || "var(--primary-green)";
  const isExecutionLoading =
    data.executionStatus === "running" || data.executionStatus === "pending";
  const isNodeLoading = isFetchingConfigs || isExecutionLoading;

  React.useEffect(() => {
    if (isNodeLoading && !expanded) {
      setShowLoadingBorder(true);
      return;
    }

    if (expanded) {
      setShowLoadingBorder(false);
      return;
    }

    const timer = setTimeout(() => setShowLoadingBorder(false), 350);
    return () => clearTimeout(timer);
  }, [isNodeLoading, expanded]);

  // Handle delete click
  const handleDelete = (id: string) => {
    setIsDelete(true);
    setDeleteNodeId(id);
  };

  const confirmDelete = () => {
    if (deleteNodeId && data.onDelete) {
      data.onDelete(deleteNodeId);
    }
    setIsDelete(false);
    setDeleteNodeId(null);
  };

  const handleFetchConfigs = async () => {
    const pluginId =
      data.pluginId ||
      sessionStorage.getItem("meshPluginId") ||
      sessionStorage.getItem("editPluginId");

    if (!pluginId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Plugin ID not found. Please save the plugin first.",
      });
      return;
    }

    setIsFetchingConfigs(true);
    try {
      const response = await fetchConfigPage(pluginId, "temp");
      if (response && response.data) {
        const dataKeys = Object.keys(response.data);
        if (dataKeys.length > 0) {
          const tabId = dataKeys[0];
          const configArray = response.data[tabId];
          if (Array.isArray(configArray)) {
            const transformedConfigs = configArray.map((c: any) => ({
              ...c,
              tabId,
              type: "temp",
              configType: "temp",
            }));
            setLocalConfigs(transformedConfigs);
          } else {
            toast({
              title: "No configurations",
              description: "No configurations found for this plugin.",
            });
          }
        } else {
          toast({
            title: "No configurations",
            description: "No configurations found for this plugin.",
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch configs:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch configurations.",
      });
    } finally {
      setIsFetchingConfigs(false);
    }
  };

  const handleSelectConfig = (
    configUid: string,
    options?: { silent?: boolean },
  ) => {
    if (configUid === "none") {
      userClearedSelectionRef.current = true; // remember this was deliberate

      if (data.onDataChange) {
        data.onDataChange(id, {
          configData: null,
          header: "Configuration",
          isDeleted: false,
          lastUpdated: Date.now(),
          parameters: {
            ...data.parameters,
            Uid: null,
            uid: null,
            TabId: null,
            tabId: null,
            title: "Configuration",
          },
        });

        if (!options?.silent) {
          toast({
            variant: "warning",
            title: "No configuration selected",
            description: "The node is no longer linked to a configuration",
          });
        }
      }
      return;
    }

    userClearedSelectionRef.current = false; // picking a real config cancels the "cleared" state

    const selectedConfig = localConfigs.find((c) => c.uid === configUid);
    if (selectedConfig && data.onDataChange) {
      data.onDataChange(id, {
        configData: selectedConfig,
        header: selectedConfig.title,
        isDeleted: false,
        lastUpdated: Date.now(),
        parameters: {
          ...data.parameters,
          Uid: selectedConfig.uid,
          uid: selectedConfig.uid,
          TabId: selectedConfig.tabId,
          tabId: selectedConfig.tabId,
          title: selectedConfig.title,
          configType: "temp",
        },
      });

      if (!options?.silent) {
        toast({
          variant: "success",
          title: `"${selectedConfig.title}" configuration selected`,
          description: `Configuration ${selectedConfig.title} is now selected for this node`,
        });
      }
    }
  };

  // A selection only counts as valid if it actually matches one of the
  // currently available configs — this is what should drive what the
  // dropdown displays, not the isDeleted flag alone.
  const hasValidSelectedConfig =
    !!selectedUid && localConfigs.some((c) => c.uid === selectedUid);

  const renderRestorationSection = () => (
    <div className="mt-1 mb-2 border p-2 rounded-md bg-muted">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-muted-foreground">
          {isDeleted || Object.keys(displayConfigData).length == 0
            ? "Select a"
            : "Replace"}{" "}
          Configurations
        </p>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/create-node-plugin/ui?edit=1&create-config=1");
            }}
            className="nodrag flex items-center justify-center h-5 w-5 rounded-full bg-muted hover:text-[var(--primary-green)] text-muted-foreground hover:text-primary transition-colors"
            title="Create a new configuration"
          >
            <Plus size={14} />
          </button>
          <button
            onClick={handleFetchConfigs}
            disabled={isFetchingConfigs}
            className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
            title="Refresh configurations"
          >
            <RefreshCw
              size={14}
              className={isFetchingConfigs ? "animate-spin" : ""}
            />
          </button>
        </div>
      </div>

      {isFetchingConfigs && localConfigs.length === 0 ? (
        <div className="flex items-center justify-center py-2 gap-2 text-[10px] text-muted-foreground">
          fetching configuration...
        </div>
      ) : localConfigs.length > 0 ? (
        <div className="space-y-2">
          <Select
            onValueChange={handleSelectConfig}
            value={hasValidSelectedConfig ? selectedUid : undefined}
            disabled={isFetchingConfigs}
          >
            <SelectTrigger className="h-8 text-[10px] w-full">
              <SelectValue
                placeholder={
                  hasValidSelectedConfig
                    ? undefined
                    : isDeleted
                      ? "Choose Configuration"
                      : "Select Configuration"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value="none"
                className="text-[10px] font-medium focus:text-destructive"
              >
                No Configuration (Unselect)
              </SelectItem>
              {localConfigs.map((config: any) => (
                <SelectItem
                  key={config.uid}
                  value={config.uid}
                  className="text-[10px]"
                >
                  {config.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-2">
          <p className="text-[9px] text-muted-foreground text-center">
            {isDeleted
              ? "No configurations are available "
              : "No configurations found"}
          </p>
          {isDeleted ? (
            <Button
              className="h-8 text-background text-[10px] px-4 gap-1.5 bg-primary"
              onClick={() =>
                navigate("/create-node-plugin/ui?edit=1&create-config=1")
              }
            >
              Create a new Configuration
            </Button>
          ) : (
            <Button
              className="h-8 text-background text-[10px] px-4 gap-1.5 bg-primaryGreen"
              onClick={handleFetchConfigs}
              disabled={isFetchingConfigs}
            >
              {isFetchingConfigs ? (
                <>
                  <RefreshCw size={12} className="animate-spin" />
                  fetching...
                </>
              ) : (
                "Retry Fetch"
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        border: showLoadingBorder
          ? "1px solid var(--light-gray)"
          : `1px solid ${data?.borderColor || "var(--primary-green)"}`,
        boxShadow: showLoadingBorder
          ? "none"
          : `0 1px 15px 1px ${data?.shadowColor || "rgba(74, 222, 128, 0.3)"} `,
      }}
      className={`relative bg-background border shadow-lg rounded-2xl p-2 text-center font-semibold transition-all ${
        expanded
          ? "w-80 p-4"
          : "w-40 h-28 p-6 max-w-80 flex items-center justify-center"
      }`}
      onContextMenu={handleContextMenu}
    >
      {showLoadingBorder && (
        <>
          <style>
            {`
        @keyframes spin-border {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-border {
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
        }

        .loading-border::before {
          content: '';
          position: absolute;
          inset: -50%;
          background: conic-gradient(
            from 0deg,
            transparent 0deg,
            transparent 300deg,
            ${loadingBorderColor} 340deg,
            ${loadingBorderColor} 360deg
          );
          animation: spin-border 2s linear infinite;
        }

        .loading-border::after {
          content: '';
          position: absolute;
          inset: 2px;
          background: var(--background);
          border-radius: inherit;
        }
      `}
          </style>

          <div className="loading-border" />
        </>
      )}
      <Confirmation
        text={"Are you sure you want to delete this node?"}
        confirmAction={confirmDelete}
        rejectAction={() => setIsDelete(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={isDelete}
        closeDialog={() => setIsDelete(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(expanded, () =>
            setExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      <div
        className={`relative z-10 flex items-center justify-between gap-4 rounded-md transition-all duration-500 ${
          expanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Fingerprint
              className={
                data.executionStatus === "running" ||
                data.executionStatus === "pending" ||
                isFetchingConfigs
                  ? "animate-pulse"
                  : ""
              }
            />
          }
          label="Configuration"
          title={
            isFetchingConfigs
              ? "Configuration"
              : isDeleted
                ? `${data.header} (Deleted)`
                : data.header
          }
          status={
            isDeleted
              ? ""
              : !data.configData || Object.keys(data.configData).length === 0
                ? "No Config"
                : ""
          }
          statusType={
            isFetchingConfigs || isExecutionLoading
              ? "loading"
              : isDeleted
                ? "error"
                : !data.configData || Object.keys(data.configData).length === 0
                  ? "warning"
                  : "success"
          }
          iconSize={35}
          borderColor={data?.borderColor || "var(--primary)"}
          onToggle={() => setExpanded((prev) => !prev)}
          isExpanded={expanded}
          executionStatus={data.executionStatus}
        />
      </div>

      {/* Configuration data display */}
      {expanded && (
        <div className="mt-3 pt-3 border-t">
          {renderRestorationSection()}

          {isDeleted ? (
            <div className="p-4 rounded-md bg-muted/20 border border-dashed border-destructive/50">
              <div className="text-xs text-destructive text-center font-medium">
                Configuration Deleted
              </div>
              <div className="text-[10px] text-muted-foreground text-center mt-1">
                {localConfigs.length > 0
                  ? "Please select another configuration from the list above to restore this node."
                  : "No other configurations available. Please create one in UI Pages."}
              </div>
            </div>
          ) : Object.keys(displayConfigData).length > 0 ? (
            <>
              <div className="text-xs pb-2 text-muted-foreground">
                {data.header} : configuration data
              </div>
              <div className="p-3 rounded-md bg-muted/20 border border-border max-h-60 overflow-y-auto nowheel nodrag">
                <div className="space-y-2">
                  {Object.entries(displayConfigData).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-start gap-2 pb-2 border-b border-border/50 last:border-0"
                    >
                      <span
                        draggable={true}
                        onDragStart={(e) => {
                          e.stopPropagation();
                          e.dataTransfer.effectAllowed = "copy";
                          e.dataTransfer.setData(
                            "application/json",
                            JSON.stringify({
                              path: `{{config.${key}}}`,
                              value,
                            }),
                          );
                          document.body.style.cursor = "grabbing";
                        }}
                        onDragEnd={() => {
                          document.body.style.cursor = "default";
                        }}
                        className="nodrag cursor-grab active:cursor-grabbing hover:bg-lightGray hover:shadow-sm px-1.5 py-0.5 rounded font-semibold text-primary select-none transition-all border border-transparent hover:border-primary/40 text-xs font-mono"
                        title={`Drag to use: {{config.${key}}}`}
                      >
                        {key}
                      </span>
                      <span className="text-xs text-muted-foreground">:</span>
                      <span className="text-xs flex justify-start text-primaryGreen break-all font-mono flex-1">
                        {typeof value === "string"
                          ? value
                          : typeof value === "object"
                            ? JSON.stringify(value, null, 2)
                            : String(value)}
                      </span>

                      <span className="ml-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`{{config.${key}}}`);
                            toast({
                              variant: "info",
                              title: "Copied to clipboard",
                              description: `{{config.${key}}} has been copied`,
                            });
                          }}
                          className="text-muted-foreground hover:text-primary transition-colors"
                          title={`Copy {{config.${key}}} to clipboard`}
                        >
                          <Copy size={14} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="p-4 rounded-md bg-muted/20 border border-dashed border-muted-foreground/50">
              <div className="text-xs text-muted-foreground text-center">
                No configuration data available
              </div>
            </div>
          )}
        </div>
      )}

      {/* Left handle - Input */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
        style={{ left: -8, zIndex: 20 }}
      />

      {/* Right handle - Output */}
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
        style={{ right: -8, zIndex: 20 }}
      />
    </div>
  );
};

export default ConfigutrationNode;
